import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plane, ArrowLeft, Lock, Mail, Eye, EyeOff, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { DROPZONES, hasSwitcher, currentDropzone, switchDropzone } from '../utils/dropzones.js';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'customer';

  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/app/dashboard';
  const { login, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loggedInUser = await login({
        email: formData.email,
        password: formData.password,
        preferredView: type === 'staff' ? 'staff' : 'customer',
      });

      // If user came via staff login but has NO staff profile at all, block
      if (type === 'staff' && !loggedInUser?.hasStaffProfile) {
        await logout();
        toast.error(t('auth.staffLoginCustomerError') || 'This is the staff login. Please use the customer login to access your account.');
        setLoading(false);
        return;
      }

      toast.success(t('auth.welcomeBack'));
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/50 z-10" />
        <img 
          src="https://images.pexels.com/photos/10762320/pexels-photo-10762320.jpeg?w=1920&q=80"
          alt="Skydiving background"
          className="w-full h-full object-cover opacity-20"
        />
      </div>

      {/* Back to home */}
      <Link 
        to="/"
        className="fixed top-6 left-6 z-50 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>{t('common.backToHome')}</span>
      </Link>

      {/* Login Form */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md mx-auto px-6 relative z-20"
      >
        <Card className="glass-card border-border/50">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Plane className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold">
              {type === 'staff' ? t('auth.staffLogin') : t('auth.customerLogin')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('auth.loginSubtitle')}
            </CardDescription>
            {/* On a group host the same form can authenticate against any
                field's backend. Which one is not guessable from the page, and
                credentials typed at the wrong field are a real mistake to make,
                so name the destination and let it be changed here. */}
            {hasSwitcher() && (
              <div className="pt-2">
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5" />
                  <span>{t('auth.signingInAt')}</span>
                  <span className="font-medium text-foreground">{currentDropzone()?.name || t('sidebar.dropzoneUnknown')}</span>
                </div>
                {/* Land on the other field's login, carrying ?type= so a staff
                    sign-in stays a staff sign-in. Never the full current URL:
                    a deep link would carry an id from this field's database to
                    a host that cannot resolve it. */}
                {DROPZONES.length > 1 && (
                  <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                    {DROPZONES.filter((dz) => dz.slug !== currentDropzone()?.slug).map((dz) => (
                      <button
                        key={dz.slug}
                        type="button"
                        onClick={() => switchDropzone(dz.slug, { path: '/login' + window.location.search })}
                        className="rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                      >
                        {dz.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t('auth.emailLabel')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder= {type === 'staff' ? "staff@dropzone.com":'customer@dropzone.com'}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t('auth.passwordLabel')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.passwordLabel')}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover:shadow-primary/30 transition-all"
                disabled={loading}
              >
                {loading ? t('auth.loggingIn') : t('common.login')}
              </Button>
            </form>

            {type === 'customer' && (
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('auth.noAccount')}{' '}
                  <Link to={`/signup?type=${type}`} className="text-primary hover:underline font-medium">
                    {t('auth.signUp')}
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div> 
    </div>
  );
};

export default LoginPage;